// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionSuccessPage.tsx

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../../redux/store";
import routes from "../../../../../routes/routes";
import { parseSubscriptionQuery } from "../../common/url/subscriptionQuery";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Button } from "../../../../../components/ui/button";
import { CheckCircle2, Calendar, FolderOpen } from "lucide-react";
import { useGetSubscriptionByCustomerAgreementIdQuery } from "../../../../../api/payments/paymentsApiSlice";
import { Spinner } from "../../../../../components/ui/spinner";

/**
 * SubscriptionSuccessPage Component
 * ---------------------------------------------------------------
 * Displays success message after subscription payment and prompts user
 * to optionally create their first booking.
 *
 * @remarks
 * This page:
 * - Shows subscription confirmation with plan details
 * - Offers two options: "Create First Booking" or "View Subscriptions"
 * - Navigates to service selection (existing single-booking flow)
 * - Maintains clean URL state transition
 *
 * @example
 * Route: /key-handover/subscription/success?locationId=loc-123&plan=monthly&...
 */
const SubscriptionSuccessPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );
    const selectedLocation = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedLocation
    );
    const appliedVoucher = useSelector(
        (state: RootState) => state.subscription.newSubscription.appliedVoucher
    );
    const subscriptionId = useSelector(
        (state: RootState) => state.bookingCreation.payment.subscriptionId
    );

    // Fetch subscription details if we have a subscriptionId from payment
    const { data: fetchedSubscriptionResponse, isLoading: isLoadingSubscription } = 
        useGetSubscriptionByCustomerAgreementIdQuery(subscriptionId || "", {
            skip: !subscriptionId,
        });
    
    // Extract data from response wrapper
    const fetchedSubscription = fetchedSubscriptionResponse?.data;

    const subscriptionQuery = parseSubscriptionQuery(location.search);

    // Use fetched data if available (after payment), otherwise use Redux state (no payment/free)
    const planToDisplay = fetchedSubscription?.commercial_model || selectedPlan;
    const locationToDisplay = fetchedSubscription?.commercial_model?.location || selectedLocation;
    
    // Calculate display amount
    const displayAmount = fetchedSubscription?.discountedAmount 
        ? parseFloat(fetchedSubscription.discountedAmount)
        : (appliedVoucher ? appliedVoucher.newAmount : selectedPlan?.price ?? 0);
    const originalAmount = fetchedSubscription?.originalAmount 
        ? parseFloat(fetchedSubscription.originalAmount)
        : appliedVoucher?.originalAmount;

    // Currency formatter based on current locale
    const formatCurrency = React.useMemo(() => {
        return new Intl.NumberFormat(i18n.language || "de", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
        });
    }, [i18n.language]);

    // Show loading state while fetching subscription details
    if (isLoadingSubscription) {
        return (
            <KeyHandoverPageLayout
                headerText={t("book_location.keys.subscription_success.header_text", {
                    defaultValue: "Subscription Activated",
                })}
                hideBackButton
            >
                <KeyHandoverContentWrapper>
                    <div className="flex items-center justify-center py-12">
                        <Spinner />
                    </div>
                </KeyHandoverContentWrapper>
            </KeyHandoverPageLayout>
        );
    }

    const handleCreateFirstBooking = () => {
        // Navigate to service selection with locationId and customerAgreementId from current context
        const locationId = subscriptionQuery.locationId || locationToDisplay?.id;
        const customerAgreementId = subscriptionId;
        const params = new URLSearchParams();
        if (locationId) params.set("locationId", locationId);
        if (customerAgreementId) params.set("customerAgreementId", customerAgreementId);
        navigate({
            pathname: routes.keyHandoverServiceSelectionPath,
            search: `?${params.toString()}`,
        });
    };

    const handleViewSubscriptions = () => {
        // Navigate to orders page (subscriptions tab)
        navigate(routes.orders);
    };

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_success.header_text", {
                defaultValue: "Subscription Activated",
            })}
            hideBackButton
        >
            <KeyHandoverContentWrapper>
                <div className="flex flex-col items-center justify-center space-y-6 py-8 text-center">
                    {/* Success Icon */}
                    <div className="rounded-full bg-green-50 p-4">
                        <CheckCircle2 className="h-16 w-16 text-green-600" />
                    </div>

                    {/* Success Message */}
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-slate-900">
                            {t("book_location.keys.subscription_success.title", {
                                defaultValue: "Your subscription is now active!",
                            })}
                        </h2>
                        <p className="text-sm text-slate-600">
                            {t("book_location.keys.subscription_success.subtitle", {
                                defaultValue:
                                    "You can now enjoy exclusive benefits and create bookings.",
                            })}
                        </p>
                    </div>

                    {/* Subscription Details Card */}
                    <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                                {t("book_location.keys.subscription_success.plan", {
                                    defaultValue: "Plan",
                                })}
                            </span>
                            <span className="text-sm text-slate-900 font-semibold">
                                {planToDisplay?.name || "-"}
                            </span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                                {t("book_location.keys.subscription_success.price", {
                                    defaultValue: "Price",
                                })}
                            </span>
                            <span className="text-sm text-slate-900 font-semibold">
                                {formatCurrency.format(displayAmount)}
                                {originalAmount && originalAmount > displayAmount && (
                                    <span className="ml-2 text-xs text-green-600 line-through">
                                        {formatCurrency.format(originalAmount)}
                                    </span>
                                )}
                            </span>
                        </div>

                        {locationToDisplay && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-700">
                                    {t("book_location.keys.subscription_success.location", {
                                        defaultValue: "Location",
                                    })}
                                </span>
                                <span className="text-sm text-slate-900 font-semibold">
                                    {locationToDisplay.name}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Call-to-Action Buttons */}
                    <div className="w-full max-w-md space-y-3 pt-4">
                        <Button onClick={handleCreateFirstBooking} className="w-full" size="lg">
                            <Calendar className="mr-2 h-5 w-5" />
                            {t("book_location.keys.subscription_success.create_booking", {
                                defaultValue: "Create Your First Booking",
                            })}
                        </Button>

                        <Button
                            onClick={handleViewSubscriptions}
                            variant="outline"
                            className="w-full"
                            size="lg"
                        >
                            <FolderOpen className="mr-2 h-5 w-5" />
                            {t("book_location.keys.subscription_success.view_subscriptions", {
                                defaultValue: "View My Subscriptions",
                            })}
                        </Button>
                    </div>

                    {/* Helper Text */}
                    <p className="text-xs text-slate-500 max-w-md pt-2">
                        {t("book_location.keys.subscription_success.helper_text", {
                            defaultValue:
                                "You can create bookings anytime from your subscriptions page.",
                        })}
                    </p>
                </div>
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionSuccessPage;
